"use client"

import type React from "react"
import { useState } from "react"
import { X, Upload, Plus } from "lucide-react"

interface AddResourceModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (resource: NewResource) => void
}

export interface NewResource {
  title: string
  description: string
  type: string
  targetClass: string
  fileName: string
}

export function AddResourceModal({ isOpen, onClose, onSubmit }: AddResourceModalProps) {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [type, setType] = useState("PDF")
  const [targetClass, setTargetClass] = useState("JSS 1")
  const [fileName, setFileName] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log("Submitting new resource:", { title, description, type, targetClass, fileName })

    onSubmit({
      title,
      description,
      type,
      targetClass,
      fileName: fileName || "resource-file.pdf",
    })

    // Reset form
    setTitle("")
    setDescription("")
    setType("PDF")
    setTargetClass("JSS 1")
    setFileName("")
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-teal-600 px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">Add New Resource</h2>
            <button 
              onClick={onClose} 
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Content */}
          <form onSubmit={handleSubmit} className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resource Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="Enter resource title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={4}
                  placeholder="Describe the resource..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition resize-none"
                />
              </div>

              {/* Resource Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resource Type *</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                >
                  <option value="PDF">PDF Document</option>
                  <option value="Video">Video</option>
                  <option value="Quiz">Quiz</option>
                  <option value="Worksheet">Worksheet</option>
                  <option value="Presentation">Presentation</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Target Class */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Class *</label>
                <select
                  value={targetClass}
                  onChange={(e) => setTargetClass(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                >
                  <option value="JSS 1">JSS 1</option>
                  <option value="JSS 2">JSS 2</option>
                  <option value="JSS 3">JSS 3</option>
                  <option value="SS 1">SS 1</option>
                  <option value="SS 2">SS 2</option>
                  <option value="SS 3">SS 3</option>
                </select>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload File</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-teal-400 transition bg-gray-50">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-2">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500">PDF, DOC, PPT, MP4, or ZIP (Max 50MB)</p>
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        setFileName(file.name)
                        console.log("File selected:", file.name)
                      }
                    }}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-block mt-4 px-4 py-2 bg-teal-50 text-teal-600 rounded-lg cursor-pointer hover:bg-teal-100 transition focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    Choose File
                  </label>
                  {fileName && (
                    <p className="mt-3 text-sm text-gray-700">
                      Selected: <span className="font-medium text-teal-600">{fileName}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200 -mx-6 -mb-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition font-medium flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <Plus className="w-5 h-5" />
                Add Resource
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}